import { ChangeDetectionStrategy, Component, Signal } from '@angular/core';
import { AccountService } from '../../../account/services/account.service';
import { DictionaryService } from '../../../dictionary/services/dictionary.service';
import { select, Store } from '@ngrx/store';
import { AppState } from '../../../reducers';
import { Locale } from '../../../dictionary/models/locale.model';
import { Settings } from '../../../account/models/settings.model';
import { mergeMap, take, tap } from 'rxjs/operators';
import { forkJoin, Observable, Subscription } from 'rxjs';
import {
  accountSelector,
  settingsSelector,
} from '../../../account/store/account.selector';
import { Account } from '../../../account/models/account.model';
import { localesSelector } from '../../../dictionary/store/dictionary.selectors';
import { DictionaryResponseDto } from '../../../dictionary/models/dictionary-response.dto';
import { SecurityCompleted } from '../../store/core.actions';
import { securityCompletedSelector } from '../../store/core.selectors';
import { LocaleDefaultChange } from '../../../dictionary/store/dictionary.actions';
import { LocalStorageHelper } from '../../helpers/local-storage.helper';
import {
  StartInactivityTime,
  StartRefreshTokenTime,
} from '../../../auth/store/auth.actions';
import { environment } from '../../../../environments/environment';

@Component({
  selector: 'app-core-main-template',
  template: `
    @if (!securityComplete()) {
      <mat-progress-bar
        class="main-progress-bar"
        mode="indeterminate" />
    }
    @if (securityComplete()) {
      <div>
        <router-outlet />
      </div>
    }
    `,
  styleUrls: ['./core-main-template.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class CoreMainTemplateComponent {
  settings$: Observable<Settings> = this.store.pipe(
    select(accountSelector),
    mergeMap((account: Account) =>
      this.accountService.getAccountSettings(account.id).pipe(
        tap({
          next: () => {
            this.localesSubscription.add(this.locales$.subscribe());
            this.settingsSubscription.unsubscribe();
          },
          error: () => {
            location.href = `${environment.myUrl}`;
          },
        })
      )
    )
  );
  locales$: Observable<Locale[]> = this.dictionaryService.getLocales().pipe(
    tap({
      next: () => {
        this.dictionarySubscription.add(this.dictionary$.subscribe());
        this.localesSubscription.unsubscribe();
      },
      error: () => {
        location.href = `${environment.myUrl}`;
      },
    })
  );
  dictionary$: Observable<DictionaryResponseDto> = forkJoin([
    this.store.pipe(select(localesSelector), take(1)),
    this.store.pipe(select(settingsSelector), take(1)),
    this.store.pipe(select(accountSelector), take(1)),
  ]).pipe(
    mergeMap(([locales, settings, account]) => {
      let localePreferred = locales.find(
        locale => locale.localeid === settings.preferredLanguage.toLowerCase()
      );
      if (!localePreferred) {
        localePreferred = locales.find(locale => locale.localedefault);
      }
      if (!localePreferred) {
        localePreferred = locales[0];
      }
      this.store.dispatch(new LocaleDefaultChange({ data: localePreferred }));
      return this.dictionaryService.getDictionary(localePreferred).pipe(
        tap({
          next: () => {
            this.store.dispatch(new SecurityCompleted());
            this.dictionarySubscription.unsubscribe();
          },
          error: () => {
            location.href = `${environment.myUrl}`;
          },
        })
      );
    })
  );
  securityComplete: Signal<boolean> = this.store.selectSignal(
    securityCompletedSelector
  );
  readonly settingsSubscription = new Subscription();
  readonly localesSubscription = new Subscription();
  readonly dictionarySubscription = new Subscription();

  constructor(
    private readonly accountService: AccountService,
    private readonly dictionaryService: DictionaryService,
    private readonly store: Store<AppState>,
  ) {
    // Clear Local Storage information of Single Page Tenant Creation
    LocalStorageHelper.clearTenantCreationFormData();
    this.store.dispatch(new StartRefreshTokenTime());
    this.store.dispatch(new StartInactivityTime());
    this.settingsSubscription.add(this.settings$.subscribe());
  }
}
